import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/components/DatePicker";

const FILING = [["single", "Single or Married filing separately"], ["married_joint", "Married filing jointly / Qualifying surviving spouse"], ["head_of_household", "Head of household"]];
const NUM = [["dependents", "Qualifying children under 17", "× $2,000"], ["other_dependents", "Other dependents", "× $500"], ["other_income", "Other income (not from jobs), $/yr", "Step 4(a)"], ["deductions", "Deductions beyond standard, $/yr", "Step 4(b)"], ["extra_withholding", "Extra federal withholding per paycheck, $", "Step 4(c)"], ["state_allowances", "State allowances", ""], ["state_extra_withholding", "Extra state withholding per paycheck, $", ""]];

export function W4Card({ employeeName }) {
  const [w4, setW4] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/me/w4").then((r) => { setW4(r.data); setF({ ...r.data, signed_name: "" }); }).catch(() => {}); }, []);
  if (!f) return null;

  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f }; NUM.forEach(([k]) => { body[k] = Number(f[k] || 0); });
      const { data } = await api.put("/me/w4", body); setW4(data); setF({ ...data, signed_name: "" });
      toast.success("W-4 saved — your next paycheck will use these elections");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const download = async () => {
    try { const r = await api.get("/me/w4/pdf", { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "W4.pdf"; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Could not download your W-4 PDF"); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="w4-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-base md:text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Tax withholding (Form W-4)</h2>
          <p className="text-xs text-muted-foreground mt-1" data-testid="w4-status">{w4?.on_file ? `On file · last updated ${fmtDate((w4.updated_at || "").slice(0, 10))} · work state ${w4.work_state}` : `No W-4 elections on file — payroll is using single with no adjustments (work state ${w4?.work_state}).`}</p>
        </div>
        {w4?.on_file && <Button size="sm" variant="outline" data-testid="w4-download-pdf" onClick={download}><Download className="w-4 h-4 mr-1.5" /> Download signed PDF</Button>}
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Step 1(c) — Filing status</Label>
        <div className="grid sm:grid-cols-3 gap-2">
          {FILING.map(([v, l]) => <button key={v} type="button" data-testid={`w4-filing-${v}`} onClick={() => set("filing_status", v)} className={`text-left text-sm rounded-lg border px-3 py-2 transition-colors ${f.filing_status === v ? "border-primary bg-primary/5 font-medium" : "border-border hover:bg-accent"}`}>{l}</button>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {NUM.map(([k, l, hint]) => <div key={k} className="space-y-1"><Label className="text-xs">{l} {hint && <span className="text-muted-foreground">({hint})</span>}</Label><Input type="number" min="0" step={k.includes("dependents") || k === "state_allowances" ? "1" : "0.01"} data-testid={`w4-${k}`} value={f[k] ?? 0} onChange={(e) => set(k, e.target.value)} /></div>)}
      </div>
      <div className="flex flex-wrap gap-5 text-sm">
        <label className="flex items-center gap-2"><Checkbox data-testid="w4-multiple-jobs" checked={!!f.multiple_jobs} onCheckedChange={(v) => set("multiple_jobs", !!v)} /> Step 2(c): I hold more than one job / spouse also works</label>
        <label className="flex items-center gap-2"><Checkbox data-testid="w4-exempt" checked={!!f.exempt} onCheckedChange={(v) => set("exempt", !!v)} /> Claim exempt from federal withholding</label>
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end border-t pt-4">
        <div className="space-y-1"><Label className="text-xs">Step 5 — Sign by typing your full legal name{employeeName ? ` (${employeeName})` : ""}</Label><Input data-testid="w4-signed-name" placeholder="Full legal name" value={f.signed_name || ""} onChange={(e) => set("signed_name", e.target.value)} /></div>
        <Button data-testid="w4-save-btn" onClick={save} disabled={saving || !f.signed_name}><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save W-4"}</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Under penalties of perjury, you declare this certificate is true, correct and complete. Your HR manager is emailed a change notice to review before the next payroll.</p>
    </Card>
  );
}
